import type {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { blend } from '@mui/system/colorManipulator';
import { Tooltip } from '@/primitives';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import { StarIcon } from '@/primitives/icons';
import { slate, white } from '@/lib/colours';
import { DEFAULT_CURRENCY_SYMBOL } from '@/lib/currency';
import EbvGroupHeader from '@/components/publicSearch/searchSection/EbvGroupHeader';
import { formatEbvAccuracyLabel } from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { EbvTraitHeaderLabelFromConfig } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import {
  type EbvTraitHeaderContext,
  getEbvConfigTraitKey,
} from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import { logError } from '@/lib/logger';
import { displayPlainText } from '@/utils/stripHtmlForTooltip';
import {
  buildBlackRedBreedAverageRaw,
  isDualLineBreedAverageRaw,
  parseBreedAverageStyledLines,
  isRedBreedAverageDisplayLine,
  resolveBreedAverageLineDisplayColor,
} from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import { shouldStackBlackRedBreedAverages } from '@/components/publicSearch/animal/ebv/breedAverageLabels';
import {
  BLACK_RED_ANGUS_CATEGORY_TINT,
  BLACK_RED_ANGUS_COLOUR_SLOT_CATEGORY,
  BLACK_RED_ANGUS_COLOUR_SLOT_TINT,
} from './blackRedAngusEbvCategories';

/** Synthetic grid row id for the breed-average summary row. */
export const BREED_AVERAGE_ROW_ID = -1;

/** Fixed height for the breed-average row (two stacked B/R EPD lines or dual captions). */
export const EBV_BREED_AVERAGE_ROW_HEIGHT = 52;

/** Row flag: breed-average row renders stacked B/R lines (taller row). */
export const BREED_AVERAGE_DUAL_LINE_FLAG = '__breedAverageDualLine';

function splitBreedAveragePlainLines(raw: string): string[] {
  return parseBreedAverageStyledLines(raw).map((line) => line.text);
}

function renderDualLineBreedAverageCell(
  raw: string,
  format: string,
  currencySymbol: string,
) {
  const lines = parseBreedAverageStyledLines(raw);
  const formatter = createEbvValueFormatter(format, currencySymbol);
  if (lines.length <= 1) {
    const line = lines[0]?.text ?? displayPlainText(raw);
    if (!line) return '';
    return (
      <span className="public-search-ebv-value" style={{ fontSize: '12px' }}>
        {formatter(line)}
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        lineHeight: 1.1,
        width: '100%',
      }}
    >
      {lines.map((line, index) => {
        const isRedLine = isRedBreedAverageDisplayLine(line.color, index);
        return (
          <span
            key={index}
            className={[
              'public-search-ebv-value',
              isRedLine ? 'public-search-ebv-red-avg' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              fontSize: '12px',
              color: resolveBreedAverageLineDisplayColor(line.color, index),
            }}
          >
            {formatter(line.text)}
          </span>
        );
      })}
    </div>
  );
}

export type { EbvTraitHeaderContext };
export const EBV_CATEGORY = {
  INDEXES: 'Indexes',
  CALVING: 'Calving',
  GROWTH: 'Growth',
  FERTILITY: 'Fertility',
  /** CANG A-band (COL letter A) — feed efficiency, type, CBI. */
  CONFORMATION: 'Conformation',
  /** CANG Y-band (COL letter Y) — cow / maternal traits. */
  MATERNAL: 'Maternal',
  CARCASS: 'Carcass',
  OTHER: 'Other',
} as const;

export const EBV_INDEX_CATEGORY = EBV_CATEGORY.INDEXES;
export const EBV_INDEX_DISPLAY = 2;

/** Category sort order for EBV grid columns/tables — indexes lead. */
const EBV_CATEGORY_ORDER: readonly string[] = [
  EBV_CATEGORY.INDEXES,
  EBV_CATEGORY.GROWTH,
  EBV_CATEGORY.CONFORMATION,
  EBV_CATEGORY.CALVING,
  EBV_CATEGORY.FERTILITY,
  EBV_CATEGORY.CARCASS,
  EBV_CATEGORY.MATERNAL,
  EBV_CATEGORY.OTHER,
];

/** Category sort order for EBV charts/series — indexes trail. */
const EBV_CHART_CATEGORY_ORDER: readonly string[] = [
  EBV_CATEGORY.GROWTH,
  EBV_CATEGORY.CONFORMATION,
  EBV_CATEGORY.CALVING,
  EBV_CATEGORY.FERTILITY,
  EBV_CATEGORY.CARCASS,
  EBV_CATEGORY.MATERNAL,
  EBV_CATEGORY.OTHER,
  EBV_CATEGORY.INDEXES,
];

/**
 * Builds a ranking fn for `order`: returns a category's position, sorting
 * unknown or missing categories last (to `order.length`).
 */
function createEbvCategoryIndexGetter(
  order: readonly string[],
): (category?: string) => number {
  return (category) => {
    if (!category) return order.length;
    const index = order.indexOf(category);
    return index === -1 ? order.length : index;
  };
}

/** Ranks a category for EBV grid columns/tables (indexes lead). */
const getEbvCategoryIndex = createEbvCategoryIndexGetter(EBV_CATEGORY_ORDER);

/** Ranks a category for EBV charts/series (indexes trail). */
export const getEbvChartCategoryIndex = createEbvCategoryIndexGetter(
  EBV_CHART_CATEGORY_ORDER,
);

/**
 * An EBV config row is shown only when it has a positive display flag plus both
 * a field key and a label. Shared by every EBV view that filters the society
 * EBV_FIELDS configs.
 */
export function isDisplayableEbvConfig(
  item: { display?: number; field?: string; label?: string } | null | undefined,
): boolean {
  return Boolean(item && (item.display ?? 0) > 0 && item.field && item.label);
}

/** Category group header row height in EBV DataGrid / tables (px). */
export const EBV_GROUP_COLUMN_HEADER_HEIGHT = 36;
/** Trait name header row — ~3 wrapped lines + info icon (48px + 25px). */
export const EBV_TRAIT_COLUMN_HEADER_HEIGHT = 73;
export const EBV_COLUMN_HEADERS_TOTAL_HEIGHT =
  EBV_GROUP_COLUMN_HEADER_HEIGHT + EBV_TRAIT_COLUMN_HEADER_HEIGHT;

export const EBV_CONFIG_PROPERTY = {
  CATEGORY: 'category',
  DISPLAY: 'display',
} as const;

// Category color mapping - matches chart colors
export const CATEGORY_COLOR_MAP: Record<string, string> = {
  [EBV_CATEGORY.CALVING]: '#FDE047', // Warm soft yellow
  [EBV_CATEGORY.GROWTH]: '#86EFAC', // Fresh green
  [EBV_CATEGORY.FERTILITY]: '#FCA5A5', // Soft coral red
  [EBV_CATEGORY.CONFORMATION]: '#7DD3FC', // CANG A-band blue
  [EBV_CATEGORY.MATERNAL]: '#FDE047', // CANG Y-band yellow
  [EBV_CATEGORY.CARCASS]: '#7DD3FC', // Sky blue
  [EBV_CATEGORY.INDEXES]: '#D8B4FE', // Lavender
  [EBV_CATEGORY.OTHER]: '#A5B4FC', // Muted periwinkle
};

export type EbvGroupingOptions = {
  /**
   * BlackRedAngus (CANG): resolve display category and tint from COL
   * `chartColour` slot. Layout still merges traits by display category in
   * standard i6 category order (same as other societies).
   */
  useColourSlotGrouping?: boolean;
};

/** Preferred: build grouping options from {@link useEbvDisplayOptions} flags. */
export function ebvGroupingOptionsFromDisplay(
  options: { useColourSlotGrouping?: boolean } | boolean | null | undefined,
): EbvGroupingOptions {
  if (typeof options === 'boolean') {
    return { useColourSlotGrouping: options };
  }
  return { useColourSlotGrouping: Boolean(options?.useColourSlotGrouping) };
}

/** i4 EBVGraph.pas RBGYAF palette (Delphi BGR → CSS hex) — legacy i4 graph bar slots. */
const ILR2_CHART_BAR_COLOUR_MAP: Record<string, string> = {
  R: '#FF8C8C',
  B: '#8282FF',
  G: '#A0FFA0',
  Y: '#64FFFF',
  A: '#FFFF3C',
  F: '#FF77FF',
};

/** COL / Setting-14 chartColour letter when present (RBGYAF slot, not semantic category). */
function resolveEbvGroupColourLetter(
  chartColour: string | undefined,
  displayColour?: string,
): string | undefined {
  const letter = (chartColour ?? displayColour)?.trim().charAt(0).toUpperCase();
  return letter && ILR2_CHART_BAR_COLOUR_MAP[letter] ? letter : undefined;
}

/** The category colour for an EBV category, falling back to the "Other" tint. */
export function getCategoryTint(
  category?: string,
  groupingOptions?: EbvGroupingOptions,
): string {
  if (groupingOptions?.useColourSlotGrouping && category) {
    const braTint = BLACK_RED_ANGUS_CATEGORY_TINT[category];
    if (braTint) return braTint;
  }
  return (
    CATEGORY_COLOR_MAP[category ?? ''] ??
    CATEGORY_COLOR_MAP[EBV_CATEGORY.OTHER] ??
    '#A5B4FC'
  );
}

/** Category tints for grid/animal-detail styling (CANG overrides Carcass → red). */
export function getEffectiveCategoryColorMap(
  groupingOptions?: EbvGroupingOptions,
): Record<string, string> {
  if (!groupingOptions?.useColourSlotGrouping) {
    return CATEGORY_COLOR_MAP;
  }
  return { ...CATEGORY_COLOR_MAP, ...BLACK_RED_ANGUS_CATEGORY_TINT };
}

/** Stable CSS-safe token for category header/cell class names. */
export function getEbvCategoryClassSuffix(category: string): string {
  return (
    category
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'other'
  );
}

// Shared category-colour theming for EBV headers/cells, used by every EBV view
// (animal list grid, mating predictor, animal detail table) so a category looks
// identical everywhere. `paperSurface` is the literal light/dark paper tone;
// pass isDark for the darker mix. Mirrors the index (purple) highlight, just in
// each category's own colour. Kept in lockstep with PublicSearchDataGrid's
// `.ebv-highlighted` index styling.

/** Strong tint for category group/column headers. */
export function categoryHeaderBackground(
  tint: string,
  paperSurface: string,
  isDark: boolean,
): string {
  return blend(paperSurface, tint, isDark ? 0.4 : 0.48);
}

/** Subtle tint for category data cells (kept light so values stay legible). */
export function categoryCellBackground(
  tint: string,
  paperSurface: string,
  isDark: boolean,
): string {
  return blend(paperSurface, tint, isDark ? 0.22 : 0.16);
}

/** Category cell fill when its row is hovered — blends row hover over the tint. */
export function categoryCellHoverBackground(
  tint: string,
  paperSurface: string,
  hoverColor: string,
  isDark: boolean,
): string {
  return blend(
    categoryCellBackground(tint, paperSurface, isDark),
    hoverColor,
    isDark ? 0.05 : 0.06,
  );
}

/** Border colour wrapping a category block (full tint in light, softened in dark). */
export function categoryBorderColor(tint: string, isDark: boolean): string {
  return isDark ? alpha(tint, 0.5) : tint;
}

/**
 * Whether the EBV row at `index` starts/ends a run of same-category rows. EBV
 * rows are pre-sorted by category, so a category change marks a block boundary:
 * `isFirst` draws a category block's left/top border, `isLast` its right border.
 * Shared by every EBV view so category blocks render identically.
 */
export function categoryRowEdges(
  rows: ReadonlyArray<{ category?: string }>,
  index: number,
): { isFirst: boolean; isLast: boolean } {
  const category = rows[index]?.category;
  return {
    isFirst: index === 0 || rows[index - 1]?.category !== category,
    isLast: index === rows.length - 1 || rows[index + 1]?.category !== category,
  };
}

/**
 * Category-colour theming for an EBV table cell (animal-detail tables) — the
 * same visual language the animal list grid and mating predictor use (subtle
 * tinted fill + a category-coloured block border). `edges` selects which block
 * borders to draw (left on a category's first column, right on its last, top on
 * header). Pass `header: true` for title cells, which use the darker header
 * shade that matches the animal search grid's column-header cells.
 */
export function categoryCellSx(
  category: string | undefined,
  edges: {
    left?: boolean;
    right?: boolean;
    top?: boolean;
    header?: boolean;
  } = {},
  groupingOptions?: EbvGroupingOptions,
): SxProps<Theme> {
  const tint = getCategoryTint(category, groupingOptions);
  const borderLight = categoryBorderColor(tint, false);
  const borderDark = categoryBorderColor(tint, true);
  const bg = edges.header ? categoryHeaderBackground : categoryCellBackground;
  return (theme) => ({
    backgroundColor: bg(tint, white[50], false),
    boxSizing: 'border-box',
    ...(edges.left && { borderLeft: `2px solid ${borderLight}` }),
    ...(edges.right && { borderRight: `2px solid ${borderLight}` }),
    ...(edges.top && { borderTop: `2px solid ${borderLight}` }),
    ...theme.applyStyles('dark', {
      backgroundColor: bg(tint, slate[800], true),
      ...(edges.left && { borderLeftColor: borderDark }),
      ...(edges.right && { borderRightColor: borderDark }),
      ...(edges.top && { borderTopColor: borderDark }),
    }),
  });
}

/**
 * EBV JSON config sort direction (uppercase, from society settings JSON).
 * Distinct from SortOrderEnum ('asc'/'desc'), which is lowercase and used for
 * MUI DataGrid sort models and API query ordering.
 */
export const EBV_SORT_ORDER = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

export type EbvSortOrder = (typeof EBV_SORT_ORDER)[keyof typeof EBV_SORT_ORDER];

export type EbvConfigItem = {
  field: string;
  label: string;
  display: number;
  format: string;
  colour?: string;
  /** COL-row ebv_format letter (RBGYAF) for i4 percentile-graph bar colour. */
  chartColour?: string;
  sortOrder?: EbvSortOrder;
  lowValue?: string;
  topValue?: string;
  category?: string;
  categoryOrder?: number;
  /** Society EBV_FIELDS: low end of the trait axis (shown left on percentile slider). */
  leftLabel?: string;
  /** Society EBV_FIELDS: high end of the trait axis (shown right on percentile slider). */
  rightLabel?: string;
  /** A-row ebv_N_name from Setting 14 — key for EBV_DESCRIPTION (type 24) tooltips */
  traitKey?: string;
  /** Legacy capitalised variant from older configs. */
  TraitKey?: string;
};

type EbvIndexConfigProperty =
  (typeof EBV_CONFIG_PROPERTY)[keyof typeof EBV_CONFIG_PROPERTY];

type EbvIndexConfigLike = Pick<EbvConfigItem, EbvIndexConfigProperty>;

function isEbvIndexConfig(config?: EbvIndexConfigLike | null): boolean {
  return (
    config?.[EBV_CONFIG_PROPERTY.CATEGORY] === EBV_INDEX_CATEGORY ||
    config?.[EBV_CONFIG_PROPERTY.DISPLAY] === EBV_INDEX_DISPLAY
  );
}

function getEbvConfigCategory(config?: EbvIndexConfigLike | null): string {
  if (isEbvIndexConfig(config)) return EBV_INDEX_CATEGORY;
  return config?.[EBV_CONFIG_PROPERTY.CATEGORY] || EBV_CATEGORY.OTHER;
}

export type EbvDisplayCategoryInput = Pick<
  EbvConfigItem,
  'category' | 'chartColour' | 'colour' | 'display'
> & {
  field?: EbvConfigItem['field'];
};

/**
 * Display group for column tint, grid grouping, and graph bars.
 * Default: semantic category from traitKey (Setting 14).
 * BlackRedAngus: one group per COL `chartColour` letter.
 */
export function getEbvDisplayCategory(
  config?: EbvDisplayCategoryInput | null,
  groupingOptions?: EbvGroupingOptions,
): string {
  if (groupingOptions?.useColourSlotGrouping) {
    if (config?.[EBV_CONFIG_PROPERTY.DISPLAY] === EBV_INDEX_DISPLAY) {
      return EBV_INDEX_CATEGORY;
    }

    const letter = resolveEbvGroupColourLetter(
      config?.chartColour,
      config?.colour,
    );
    if (letter && BLACK_RED_ANGUS_COLOUR_SLOT_CATEGORY[letter]) {
      return BLACK_RED_ANGUS_COLOUR_SLOT_CATEGORY[letter];
    }
  } else if (isEbvIndexConfig(config)) {
    return EBV_INDEX_CATEGORY;
  }

  return getEbvConfigCategory(config);
}

/** Standard group tint — BRA uses COL paint-band tint; others use display category. */
export function getEbvDisplayTint(
  config?: EbvDisplayCategoryInput | null,
  groupingOptions?: EbvGroupingOptions,
): string {
  if (groupingOptions?.useColourSlotGrouping) {
    const letter = resolveEbvGroupColourLetter(
      config?.chartColour,
      config?.colour,
    );
    if (letter && BLACK_RED_ANGUS_COLOUR_SLOT_TINT[letter]) {
      return BLACK_RED_ANGUS_COLOUR_SLOT_TINT[letter];
    }
  }

  return getCategoryTint(
    getEbvDisplayCategory(config, groupingOptions),
    groupingOptions,
  );
}

/** Percentile / potential-change graph bar tint. */
export function resolveEbvChartBarColour(
  chartColour: string | undefined,
  category: string | undefined,
  displayColour?: string,
  groupingOptions?: EbvGroupingOptions,
): string {
  return getEbvDisplayTint(
    { chartColour, colour: displayColour, category, display: 1 },
    groupingOptions,
  );
}

/**
 * Sort EBV configs by display category order (`EBV_CATEGORY_ORDER`), then by
 * `categoryOrder` within each category. Uses colour-slot categories when set.
 */
function sortEbvConfigs(
  configs: EbvConfigItem[],
  groupingOptions?: EbvGroupingOptions,
): EbvConfigItem[] {
  return sortConfigsByCategory(configs, groupingOptions);
}

function sortConfigsByCategory(
  configs: EbvConfigItem[],
  groupingOptions?: EbvGroupingOptions,
): EbvConfigItem[] {
  return [...configs].sort((firstConfig, secondConfig) => {
    const firstCategoryIndex = getEbvCategoryIndex(
      getEbvDisplayCategory(firstConfig, groupingOptions),
    );
    const secondCategoryIndex = getEbvCategoryIndex(
      getEbvDisplayCategory(secondConfig, groupingOptions),
    );

    if (firstCategoryIndex !== secondCategoryIndex) {
      return firstCategoryIndex - secondCategoryIndex;
    }

    const firstOrder = firstConfig.categoryOrder ?? Infinity;
    const secondOrder = secondConfig.categoryOrder ?? Infinity;
    return firstOrder - secondOrder;
  });
}

/** Grid field names for selection-index columns in an EBV config. */
export function getEbvIndexFields(ebvConfig: EbvConfigItem[]): Set<string> {
  return new Set(
    ebvConfig
      .filter((config) => config?.field && isEbvIndexConfig(config))
      .map((config) => `EBV${config.field.replace('ebv', '')}`),
  );
}

/** Derive persisted EBV intent from a Manage Columns visibility change. */
export function getEbvVisibilityChanges(
  columns: ReadonlyArray<{ field: string }>,
  previousModel: Record<string, boolean>,
  nextModel: Record<string, boolean>,
  fieldToCategory: Readonly<Record<string, string>>,
  categoryVisibility: Readonly<Record<string, boolean>>,
  syncAllColumnsWhenShowingCategory?: string,
): {
  columnVisibility: Record<string, boolean>;
  categoriesToShow: string[];
  categoriesWithNewlyShownColumns: string[];
} {
  const categoriesToShow = new Set<string>();
  const categoriesWithNewlyShownColumns = new Set<string>();
  const categoriesToSync = new Set<string>();

  for (const { field } of columns) {
    if (!field.startsWith('EBV')) continue;
    const wasVisible = previousModel[field] !== false;
    const isVisible = nextModel[field] !== false;
    const category = fieldToCategory[field];
    if (!wasVisible && isVisible && category != null) {
      categoriesWithNewlyShownColumns.add(category);
      if (categoryVisibility[category] === false) {
        categoriesToShow.add(category);
        categoriesToSync.add(category);
      }
    }
  }

  const syncAllColumns =
    syncAllColumnsWhenShowingCategory != null &&
    categoriesWithNewlyShownColumns.has(syncAllColumnsWhenShowingCategory);
  const columnVisibility: Record<string, boolean> = {};
  for (const { field } of columns) {
    if (!field.startsWith('EBV')) continue;
    const wasVisible = previousModel[field] !== false;
    const isVisible = nextModel[field] !== false;
    const category = fieldToCategory[field];

    // Preserve the exact panel state for a reopened category, or for every EBV
    // column when selecting an index will auto-enable the global EBV switch.
    if (
      syncAllColumns ||
      wasVisible !== isVisible ||
      (category != null && categoriesToSync.has(category))
    ) {
      columnVisibility[field] = isVisible;
    }
  }

  return {
    columnVisibility,
    categoriesToShow: [...categoriesToShow],
    categoriesWithNewlyShownColumns: [...categoriesWithNewlyShownColumns],
  };
}

export type EbvAverageData = {
  runLabel?: string;
  avgsCaption?: string;
  avgCaptionB?: string;
  avgCaptionR?: string;
  medianCaption?: string;
  graphFooterCaption?: string;
  [key: string]: string | undefined;
};

type SocietySettingLike = {
  societySettingType?: string | null;
  value?: string | null;
};

export function parseEbvConfig(
  settings?: SocietySettingLike[] | null,
): EbvConfigItem[] {
  if (!settings || !Array.isArray(settings)) return [];
  const setting = settings.find(
    (s) => s && s.societySettingType === SOCIETY_SETTING_TYPE.EBV_FIELDS,
  );
  if (!setting?.value) return [];
  try {
    const parsed = JSON.parse(setting.value) as EbvConfigItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    logError('Failed to parse EBV columns configuration:', error);
    return [];
  }
}

export function parseEbvAverage(
  settings?: SocietySettingLike[] | null,
): EbvAverageData | null {
  if (!settings || !Array.isArray(settings)) return null;
  const setting = settings.find(
    (s) => s && s.societySettingType === SOCIETY_SETTING_TYPE.EBV_AVERAGE,
  );
  if (!setting?.value) return null;
  try {
    const parsed = JSON.parse(setting.value) as EbvAverageData & {
      title?: string;
      description?: string;
      blackCaption?: string;
      redCaption?: string;
    };
    return {
      ...parsed,
      runLabel: parsed.runLabel ?? parsed.title,
      avgsCaption: parsed.avgsCaption ?? parsed.description,
      avgCaptionB: parsed.avgCaptionB ?? parsed.blackCaption,
      avgCaptionR: parsed.avgCaptionR ?? parsed.redCaption,
    };
  } catch (error) {
    logError('Failed to parse EBV average configuration:', error);
    return null;
  }
}

/**
 * Positive accuracy only; null when missing, non-numeric, or zero.
 * Typical storage: whole-number percent 1–100 (Custom and Breedplan). CAHF is an
 * anomaly storing fractional reliability (0.26 = 26%) — handled by magnitude in formatEbvAccuracyPercent.
 * Ped Est sentinel -1 is handled in formatEbvAccuracyDisplay before this runs.
 */
function parseEbvAccuracyNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(String(raw).replace(/%/g, '').trim());
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

export type FormatEbvAccuracyOptions = {
  isBlackRedAngusStrain?: boolean;
};

export type GenerateEbvColumnsOptions = FormatEbvAccuracyOptions & {
  /** Setting-15 breed averages — used to render dual-line ILR2 HTML on the summary row. */
  ebvAverage?: EbvAverageData | null;
  /** BlackRedAngus: defaults true when isBlackRedAngusStrain is set. */
  useColourSlotGrouping?: boolean;
};

/**
 * Human-facing accuracy text for grid, detail, and export.
 * Ped Est dbo `P` is stored as -1 for BlackRedAngus strain societies.
 * BlackRedAngus (CANG): i4 shows whole numbers without a % suffix (15, 32, P).
 */
export function formatEbvAccuracyDisplay(
  raw: unknown,
  options: FormatEbvAccuracyOptions = {},
): string {
  if (raw === null || raw === undefined || raw === '') return '';

  const n = parseFloat(String(raw).replace(/%/g, '').trim());
  if (!Number.isNaN(n) && n === -1) {
    return options.isBlackRedAngusStrain ? 'P' : '';
  }

  if (options.isBlackRedAngusStrain) {
    const parsed = parseEbvAccuracyNumber(raw);
    if (parsed === null) return '';
    if (parsed > 0 && parsed < 1) return formatI4DecimalAccuracy(parsed);
    return String(Math.round(parsed));
  }

  return formatEbvAccuracyPercent(raw);
}

/** Fractional reliability (CAHF anomaly) — i4 shows `.26` not `0.26`. */
function formatI4DecimalAccuracy(n: number): string {
  const fixed = n.toFixed(2);
  return fixed.startsWith('0.') ? fixed.slice(1) : fixed;
}

/**
 * Label for tables/charts; empty when missing or 0% so callers can show an em dash.
 * Driven by stored magnitude, not ebvProvider: n < 1 → i4 decimal (CAHF anomaly);
 * n >= 1 → whole-number percent (typical Custom 1–100 and Breedplan).
 */
export function formatEbvAccuracyPercent(raw: unknown): string {
  const n = parseEbvAccuracyNumber(raw);
  if (n === null) return '';
  if (n > 0 && n < 1) return formatI4DecimalAccuracy(n);
  return `${Math.round(n)}%`;
}

export type EbvRow = {
  ID: number;
  ANIMAL_NAME?: string;
  TRAIT_LEADER?: string;
  [key: string]: unknown;
};

/** i4 formatEBV: format char → decimal places (null = currency `$`). */
export function resolveI4EbvDecimalPlaces(
  format: string | number | null | undefined,
): number | null {
  const code = String(format ?? '').trim();
  if (!code || code === '$') return null;

  switch (code.toUpperCase()) {
    case 'A':
      return 0;
    case 'B':
      return 1;
    case 'C':
      return 2;
    case 'D':
      return 3;
    default:
      if (/^[0-9]$/.test(code)) return parseInt(code, 10);
      return null;
  }
}

export function createEbvValueFormatter(
  format: string,
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
) {
  return (value: string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '';

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;

    const code = String(format ?? '').trim() || '0';

    if (code === '$') {
      if (numValue < 0) {
        return `-${currencySymbol}${Math.round(Math.abs(numValue))}`;
      }
      const sign = numValue > 0 ? '+' : '';
      return `${sign}${currencySymbol}${Math.round(numValue)}`;
    }

    const decimals = resolveI4EbvDecimalPlaces(code);
    if (decimals !== null) {
      const sign = numValue > 0 ? '+' : '';
      return `${sign}${numValue.toFixed(decimals)}`;
    }

    return value;
  };
}

export function generateEbvColumns<RowType extends EbvRow>(
  ebvConfig: EbvConfigItem[],
  showEBVs: boolean,
  showEBVAccuracy: boolean,
  showPercentiles: boolean,
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
  ebvLabel: string = 'EBV',
  traitHeaderContext?: EbvTraitHeaderContext,
  columnOptions: GenerateEbvColumnsOptions = {},
): GridColDef<RowType>[] {
  const accuracyDisplayOptions: FormatEbvAccuracyOptions = columnOptions;
  const groupingOptions: EbvGroupingOptions = {
    useColourSlotGrouping:
      columnOptions.useColourSlotGrouping ??
      columnOptions.isBlackRedAngusStrain,
  };
  const columns: GridColDef<RowType>[] = [];

  const visibleConfigs = ebvConfig.filter(isDisplayableEbvConfig);

  if (visibleConfigs.length === 0) {
    return columns;
  }

  const sortedConfigs = sortEbvConfigs(visibleConfigs, groupingOptions);

  for (const config of sortedConfigs) {
    if (!config || !config.field || !config.label) continue;

    const displayLabel = config.label.replace(/\^/g, ' ');
    const fieldNumber = config.field.replace('ebv', '');
    const valueFormatter = createEbvValueFormatter(
      config.format || '0',
      currencySymbol,
    );

    // Which sub-values a cell shows:
    // - Indexes carry only a value and follow the EBV toggle.
    // - When at least one display toggle is on, a trait EBV value follows the
    //   EBV toggle (so "Percentiles/Accuracy only, EBVs off" hides the value).
    // - When ALL display toggles are off, a manually shown trait column falls
    //   back to its headline EBV value so the cell isn't blank.
    const isIndex = isEbvIndexConfig(config);
    const anyDisplayToggleOn = showEBVs || showEBVAccuracy || showPercentiles;
    const showValue = showEBVs || (!isIndex && !anyDisplayToggleOn);

    // Non-index columns are themed by their category colour (background + border)
    // — the same visual language indexes get in purple. Indexes keep the stronger
    // `ebv-highlighted` (purple header + border + elevation) treatment instead.
    const category = getEbvDisplayCategory(config, groupingOptions);
    const categoryClass = isIndex
      ? ''
      : `ebv-cat-${getEbvCategoryClassSuffix(category)}`;

    const ebvField = `EBV${fieldNumber}`;
    const accField = `EBV${fieldNumber}_ACC`;
    const percentileField = `EBV${fieldNumber}_PERCENTILE`;
    const traitLeaderIndex = Number(fieldNumber) - 1;
    const isTraitLeaderCell = (row?: EbvRow) => {
      if (!showEBVs || traitLeaderIndex < 0) return false;
      const traitLeader = row?.TRAIT_LEADER;
      return (
        !!traitLeader &&
        traitLeader.length > traitLeaderIndex &&
        traitLeader[traitLeaderIndex] === '1'
      );
    };

    const traitKey = getEbvConfigTraitKey(config);
    const hasTraitExplanation =
      traitHeaderContext != null &&
      traitKey != null &&
      traitHeaderContext.traitExplanationsByKey.has(traitKey);

    columns.push({
      field: ebvField,
      headerName: displayLabel,
      flex: 1,
      minWidth: 80,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      ...(hasTraitExplanation && {
        renderHeader: () => (
          <EbvTraitHeaderLabelFromConfig
            label={displayLabel}
            config={config}
            traitHeaderContext={traitHeaderContext}
          />
        ),
      }),
      headerClassName: [
        'ebv-column',
        isIndex ? 'ebv-highlighted' : categoryClass,
      ]
        .filter(Boolean)
        .join(' '),
      cellClassName: (params) => {
        const classes: string[] = [];
        if (isIndex) {
          classes.push('ebv-highlighted');
        } else if (categoryClass) {
          classes.push(categoryClass);
        }

        if (isTraitLeaderCell(params.row)) {
          classes.push('ebv-trait-leader');
        }

        return classes.length > 0 ? classes.join(' ') : '';
      },
      // MUI X calls this as (value, row, column, apiRef) — not legacy { value }.
      // Wrong shape made getRowFormattedValue/export fall back to raw numbers (e.g. missing $).
      valueFormatter: (value, row) => {
        if (value === undefined || value === null || value === '') return '';
        const rawStr = String(value);
        if (
          row?.ID === BREED_AVERAGE_ROW_ID &&
          isDualLineBreedAverageRaw(rawStr)
        ) {
          const lines = splitBreedAveragePlainLines(rawStr);
          return lines
            .map((line) => valueFormatter(line))
            .filter(Boolean)
            .join(' / ');
        }
        return valueFormatter(rawStr);
      },
      renderCell: (params: GridRenderCellParams<RowType>) => {
        if (!params || !params.row) {
          return '';
        }

        const ebvValue = params.value as string | number | null | undefined;
        const settingRaw = columnOptions.ebvAverage?.[`ebv_${fieldNumber}_avg`];
        let breedAverageRaw: string | null = null;
        if (params.row.ID === BREED_AVERAGE_ROW_ID) {
          if (
            typeof ebvValue === 'string' &&
            isDualLineBreedAverageRaw(ebvValue)
          ) {
            breedAverageRaw = ebvValue;
          } else if (
            settingRaw !== undefined &&
            settingRaw !== null &&
            settingRaw !== ''
          ) {
            breedAverageRaw = String(settingRaw);
          }
        } else if (
          typeof ebvValue === 'string' &&
          isDualLineBreedAverageRaw(ebvValue)
        ) {
          breedAverageRaw = ebvValue;
        }

        if (breedAverageRaw && isDualLineBreedAverageRaw(breedAverageRaw)) {
          return renderDualLineBreedAverageCell(
            breedAverageRaw,
            config.format || '0',
            currencySymbol,
          );
        }
        const accValue = (params.row as EbvRow)[accField] as
          | string
          | number
          | null
          | undefined;
        const percentileValue = (params.row as EbvRow)[percentileField] as
          | number
          | null
          | undefined;

        const isTraitLeader = isTraitLeaderCell(params.row as EbvRow);
        const formattedEbv =
          showValue && ebvValue !== undefined && ebvValue !== null
            ? valueFormatter(String(ebvValue))
            : '';
        const formattedAcc =
          accValue !== undefined && accValue !== null && showEBVAccuracy
            ? formatEbvAccuracyDisplay(accValue, accuracyDisplayOptions)
            : '';
        const formattedPercentile =
          percentileValue !== undefined &&
          percentileValue !== null &&
          showPercentiles
            ? String(percentileValue)
            : '';

        if (!formattedEbv && !formattedAcc && !formattedPercentile) {
          if (!isTraitLeader) {
            return '';
          }

          return (
            <span
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tooltip title="Trait Leader" arrow placement="top">
                <span
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 2,
                    lineHeight: 0,
                    display: 'inline-flex',
                    cursor: 'help',
                  }}
                >
                  <StarIcon sx={{ fontSize: 16 }} />
                </span>
              </Tooltip>
            </span>
          );
        }

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1.4,
              position: 'relative',
              width: '100%',
              height: '100%',
            }}
          >
            {isTraitLeader && (
              <Tooltip title="Trait Leader" arrow placement="top">
                <span
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 2,
                    lineHeight: 0,
                    display: 'inline-flex',
                    cursor: 'help',
                  }}
                >
                  <StarIcon sx={{ fontSize: 16 }} />
                </span>
              </Tooltip>
            )}
            {formattedEbv && (
              <Tooltip title={ebvLabel} arrow placement="top">
                <span
                  className="public-search-ebv-value"
                  style={{
                    fontSize: '12px',
                  }}
                >
                  {formattedEbv}
                </span>
              </Tooltip>
            )}
            {formattedAcc && (
              <Tooltip
                title={formatEbvAccuracyLabel(ebvLabel)}
                arrow
                placement="top"
              >
                <span
                  className="public-search-ebv-detail"
                  style={{
                    fontSize: '10px',
                  }}
                >
                  {formattedAcc}
                </span>
              </Tooltip>
            )}
            {formattedPercentile && (
              <Tooltip title="Percentile" arrow placement="top">
                <span
                  className="public-search-ebv-detail"
                  style={{
                    fontSize: '10px',
                  }}
                >
                  {formattedPercentile}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    });
  }

  return columns;
}

export function generateEbvColumnGroupingModel(
  ebvConfig: EbvConfigItem[],
  groupingOptions?: EbvGroupingOptions,
): GridColumnGroupingModel {
  const visibleConfigs = ebvConfig.filter(isDisplayableEbvConfig);

  if (visibleConfigs.length === 0) {
    return [];
  }

  const sortedConfigs = sortEbvConfigs(visibleConfigs, groupingOptions);

  const categoryGroups = new Map<string, string[]>();
  for (const config of sortedConfigs) {
    if (!config || !config.field) continue;
    const category = getEbvDisplayCategory(config, groupingOptions);
    const fieldNumber = config.field.replace('ebv', '');
    const ebvField = `EBV${fieldNumber}`;

    if (!categoryGroups.has(category)) {
      categoryGroups.set(category, []);
    }
    categoryGroups.get(category)!.push(ebvField);
  }

  const groupingModel: GridColumnGroupingModel = [];
  for (const category of EBV_CATEGORY_ORDER) {
    const fields = categoryGroups.get(category);
    if (fields && fields.length > 0) {
      groupingModel.push({
        groupId: category,
        headerName: category,
        headerClassName: `ebv-group-${getEbvCategoryClassSuffix(category)}`,
        renderHeaderGroup: () => <EbvGroupHeader category={category} />,
        children: fields.map((field) => ({ field })),
      });
    }
  }

  for (const [category, fields] of categoryGroups.entries()) {
    if (!EBV_CATEGORY_ORDER.includes(category) && fields.length > 0) {
      groupingModel.push({
        groupId: category,
        headerName: category,
        headerClassName: `ebv-group-${getEbvCategoryClassSuffix(category)}`,
        renderHeaderGroup: () => <EbvGroupHeader category={category} />,
        children: fields.map((field) => ({ field })),
      });
    }
  }

  return groupingModel;
}

export type EbvConfigGroup = { category: string; items: EbvConfigItem[] };

export function getEbvConfigGroupedByCategory(
  ebvConfig: EbvConfigItem[],
  groupingOptions?: EbvGroupingOptions,
): EbvConfigGroup[] {
  const visibleConfigs = ebvConfig.filter(isDisplayableEbvConfig);

  if (visibleConfigs.length === 0) {
    return [];
  }

  const sortedConfigs = sortEbvConfigs(visibleConfigs, groupingOptions);

  const categoryGroups = new Map<string, EbvConfigItem[]>();
  for (const config of sortedConfigs) {
    if (!config || !config.field) continue;
    const category = getEbvDisplayCategory(config, groupingOptions);
    if (!categoryGroups.has(category)) {
      categoryGroups.set(category, []);
    }
    categoryGroups.get(category)!.push(config);
  }

  const result: EbvConfigGroup[] = [];
  for (const category of EBV_CATEGORY_ORDER) {
    const items = categoryGroups.get(category);
    if (items && items.length > 0) {
      result.push({ category, items });
    }
  }
  for (const [category, items] of categoryGroups.entries()) {
    if (!EBV_CATEGORY_ORDER.includes(category) && items.length > 0) {
      result.push({ category, items });
    }
  }
  return result;
}

export type CreateAverageRowOptions = {
  /** CANG: stack Black (B) and Red (R) breed averages in each EBV cell. */
  isBlackRedAngusStrain?: boolean;
};

function resolveAverageTraitRaw(
  ebvAverage: EbvAverageData,
  fieldNumber: string,
  stackBlackRed: boolean,
): string | undefined {
  const avgKey = `ebv_${fieldNumber}_avg`;
  const avgValue = ebvAverage[avgKey];

  if (
    stackBlackRed &&
    avgValue !== undefined &&
    avgValue !== null &&
    avgValue !== ''
  ) {
    const aRowRaw = String(avgValue);
    // i4 parity: CANG A-row ebv_N_avg is pre-composed B/R HTML from ILR2.
    if (isDualLineBreedAverageRaw(aRowRaw)) {
      return aRowRaw;
    }
  }

  if (stackBlackRed) {
    const dual = buildBlackRedBreedAverageRaw(
      ebvAverage[`${avgKey}_b`],
      ebvAverage[`${avgKey}_r`],
    );
    if (dual) return dual;
  }

  if (avgValue === undefined || avgValue === null || avgValue === '') {
    return undefined;
  }
  return String(avgValue);
}

export function createAverageRow<RowType extends EbvRow>(
  ebvAverage: EbvAverageData,
  ebvConfig: EbvConfigItem[],
  currencySymbol: string = DEFAULT_CURRENCY_SYMBOL,
  averageRowLabel?: string,
  options: CreateAverageRowOptions = {},
): RowType | null {
  if (!ebvAverage) return null;

  const stackBlackRed = shouldStackBlackRedBreedAverages(
    Boolean(options.isBlackRedAngusStrain),
    ebvAverage,
  );
  const label =
    averageRowLabel ??
    (!stackBlackRed ? ebvAverage.avgsCaption : undefined) ??
    ebvAverage.avgCaptionB ??
    ebvAverage.avgCaptionR;
  if (!label) return null;

  const averageRow = {
    ID: BREED_AVERAGE_ROW_ID,
    ANIMAL_NAME: label,
  } as unknown as RowType;

  const visibleConfigs = ebvConfig.filter(isDisplayableEbvConfig);

  const sortedConfigsAvg = sortConfigsByCategory(visibleConfigs);

  let hasDualLineValues = false;

  for (const config of sortedConfigsAvg) {
    if (!config || !config.field) continue;

    const fieldNumber = config.field.replace('ebv', '');
    const rawStr = resolveAverageTraitRaw(
      ebvAverage,
      fieldNumber,
      stackBlackRed,
    );

    if (rawStr !== undefined) {
      const valueFormatter = createEbvValueFormatter(
        config.format || '0',
        currencySymbol,
      );
      const ebvField = `EBV${fieldNumber}`;

      if (isDualLineBreedAverageRaw(rawStr)) {
        hasDualLineValues = true;
        (averageRow as EbvRow)[ebvField] = rawStr;
        continue;
      }

      const numValue = parseFloat(rawStr);
      if (!isNaN(numValue)) {
        (averageRow as EbvRow)[ebvField] = valueFormatter(String(numValue));
      } else {
        (averageRow as EbvRow)[ebvField] = rawStr;
      }
    }
  }

  (averageRow as EbvRow)[BREED_AVERAGE_DUAL_LINE_FLAG] =
    stackBlackRed || hasDualLineValues;

  return averageRow;
}
